import type { IConsentAccount, IConsentChallenge } from './types';

/**
 * Reads the signed consent challenge out of the query string.
 *
 * This decodes the JWT payload without verifying the signature, so every
 * claim is untrusted. Estuary verifies the token when the decision is posted
 * back, which keeps a forged payload from granting access. It does not keep a
 * forged payload from rendering, so the page shows no trust signals from it.
 */

function decodeSegment(segment: string): unknown {
  const padded = segment.replaceAll('-', '+').replaceAll('_', '/');
  const json = atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '='));
  // Handle non-ASCII (account names, partner names) that JWT encodes as UTF-8.
  const text = decodeURIComponent(
    json
      .split('')
      .map((c) => `%${(c.codePointAt(0) ?? 0).toString(16).padStart(2, '0')}`)
      .join(''),
  );
  return JSON.parse(text);
}

function isAccount(value: unknown): boolean {
  const account = value as Partial<IConsentAccount> | null;
  return (
    typeof account === 'object' &&
    account !== null &&
    typeof account.id === 'string' &&
    typeof account.name === 'string'
  );
}

function isChallenge(value: unknown): value is IConsentChallenge {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Partial<IConsentChallenge>;
  return (
    typeof candidate.client === 'object' &&
    candidate.client !== null &&
    typeof candidate.client.name === 'string' &&
    typeof candidate.client.redirect_host === 'string' &&
    Array.isArray(candidate.scopes) &&
    candidate.scopes.every((scope) => typeof scope === 'string') &&
    Array.isArray(candidate.accounts) &&
    candidate.accounts.every(isAccount) &&
    typeof candidate.default_account_id === 'string'
  );
}

export function parseChallenge(token: unknown): IConsentChallenge | undefined {
  if (typeof token !== 'string') {
    return undefined;
  }
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return undefined;
    }
    const payload = decodeSegment(parts[1]);
    return isChallenge(payload) ? payload : undefined;
  } catch {
    return undefined;
  }
}

export function isExpired(challenge: IConsentChallenge, now: number = Date.now()): boolean {
  return typeof challenge.exp === 'number' && challenge.exp * 1000 <= now;
}
